import React from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import RevaText from "components/common/RevaText";
import { formatDateHeading, MIDNIGHT_END_TIME } from "./helpers";

function WindowDialog({
  addDateToForm,
  establishments,
  fieldsForEstablishment,
  handleEndTimeChange,
  handleEndsAtMidnightChange,
  handleWindowFormChange,
  intl,
  open,
  removeDateFromForm,
  saveWindow,
  saving,
  setWindowModalOpen,
  windowForm,
  windowTimeRangeInvalid,
}) {
  return (
    <Dialog open={open} onClose={() => setWindowModalOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        {windowForm.window_id
          ? intl.formatMessage({ id: "scheduling.availability.editTitle" })
          : intl.formatMessage({ id: "scheduling.availability.add" })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {windowForm.window_id && (
            <Alert severity="info">
              {intl.formatMessage({ id: "scheduling.availability.editHelp" })}
            </Alert>
          )}
          <FormControl fullWidth size="small" required>
            <InputLabel>{intl.formatMessage({ id: "Club" })}</InputLabel>
            <Select
              value={windowForm.establishment_id}
              label={intl.formatMessage({ id: "Club" })}
              onChange={handleWindowFormChange("establishment_id")}
            >
              {establishments.map((establishment) => (
                <MenuItem key={establishment.id} value={establishment.id}>
                  {establishment.public_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>{intl.formatMessage({ id: "Cancha (opcional)" })}</InputLabel>
            <Select
              value={windowForm.field_id}
              label={intl.formatMessage({ id: "Cancha (opcional)" })}
              onChange={handleWindowFormChange("field_id")}
            >
              <MenuItem value="">{intl.formatMessage({ id: "Todas las canchas" })}</MenuItem>
              {fieldsForEstablishment.map((field) => (
                <MenuItem key={field.id} value={field.id}>
                  {field.name}{field.sports?.length ? ` · ${field.sports.map((sport) => sport.name).join(", ")}` : ""}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {intl.formatMessage({ id: "scheduling.availability.courtOptionalHelp" })}
            </FormHelperText>
          </FormControl>

          <Divider />
          <Stack spacing={0.25}>
            <RevaText variant="subtitle2" id="scheduling.availability.availableDates" />
            <RevaText variant="body2" color="text.secondary" id="scheduling.availability.multipleDatesHelp" />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="date"
              size="small"
              required
              label={intl.formatMessage({ id: "scheduling.daily.date" })}
              value={windowForm.dateInput}
              onChange={handleWindowFormChange("dateInput")}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addDateToForm();
                }
              }}
            />
            <Button variant="outlined" size="small" onClick={addDateToForm} disabled={!windowForm.dateInput || (windowForm.window_id && windowForm.dates.length > 0)}>
              {intl.formatMessage({ id: "Agregar" })}
            </Button>
          </Stack>
          {windowForm.dates.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {windowForm.dates.map((date) => (
                <Chip
                  key={date}
                  label={formatDateHeading(date, intl)}
                  onDelete={() => removeDateFromForm(date)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
          {windowForm.dates.length === 0 && (
            <RevaText variant="body2" color="text.secondary" id="Selecciona al menos una fecha" />
          )}

          <Divider />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
            <TextField
              fullWidth
              type="time"
              size="small"
              required
              label={intl.formatMessage({ id: "Hora de inicio" })}
              value={windowForm.start_time}
              onChange={handleWindowFormChange("start_time")}
              InputLabelProps={{ shrink: true }}
            />
            <Stack sx={{ width: "100%" }} spacing={0.5}>
              <TextField
                fullWidth
                type={windowForm.ends_at_midnight ? "text" : "time"}
                size="small"
                required
                label={intl.formatMessage({ id: "Hora de fin" })}
                value={windowForm.ends_at_midnight ? MIDNIGHT_END_TIME : windowForm.end_time}
                onChange={handleEndTimeChange}
                InputLabelProps={{ shrink: true }}
                inputProps={windowForm.ends_at_midnight ? { readOnly: true } : undefined}
                error={windowTimeRangeInvalid}
                helperText={
                  windowForm.ends_at_midnight
                    ? intl.formatMessage({ id: "Se guardará como 24:00 del mismo día" })
                    : windowTimeRangeInvalid
                      ? intl.formatMessage({ id: "La hora de fin debe ser posterior a la de inicio" })
                      : undefined
                }
              />
              <FormControlLabel
                control={
                  <Switch checked={windowForm.ends_at_midnight} onChange={handleEndsAtMidnightChange} size="small" />
                }
                label={intl.formatMessage({ id: "scheduling.availability.endsAtMidnightControl" })}
                sx={{ ml: 0 }}
              />
              <RevaText
                variant="caption"
                color="text.secondary"
                id="scheduling.availability.endsAtMidnightHelp"
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setWindowModalOpen(false)}>{intl.formatMessage({ id: "Cancelar" })}</Button>
        <Button
          variant="contained"
          onClick={saveWindow}
          disabled={
            saving ||
            !windowForm.establishment_id ||
            windowForm.dates.length === 0 ||
            (windowForm.window_id && windowForm.dates.length !== 1) ||
            !windowForm.start_time ||
            !windowForm.end_time ||
            windowTimeRangeInvalid
          }
        >
          {windowForm.dates.length > 1
            ? intl.formatMessage({ id: "Guardar {count} ventanas" }, { count: windowForm.dates.length })
            : intl.formatMessage({ id: "Guardar" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WindowDialog;
